import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import axios from "axios";
import { Search, ChevronDown, X } from "lucide-react";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "")}/api`;

const QuickPlanModal = ({ isOpen, onClose, date, teamId, teamName, onSuccess }) => {
  const [workPermits, setWorkPermits] = useState([]);
  const [selectedWorkPermitId, setSelectedWorkPermitId] = useState("");
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("16:00");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch pending orders (work permits with status pending)
  useEffect(() => {
    const fetchPendingOrders = async () => {
      if (!isOpen) return;

      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          toast.error("No access token found");
          return;
        }

        const config = {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: "pending" },
        };

        const response = await axios.get(`${API_BASE_URL}/workpermit/`, config);
        setWorkPermits(response.data || []);
      } catch (error) {
        console.error("Error fetching pending orders:", error);
        toast.error("Failed to load pending orders");
      }
    };

    fetchPendingOrders();
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedWorkPermitId("");
      setStartTime("08:00");
      setEndTime("16:00");
      setSearchTerm("");
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Filter orders based on search term
  const filteredWorkPermits = workPermits.filter((wp) => {
    const searchFields = [
      wp.order_number,
      wp.work_permit_number,
      wp.turbine_data?.wind_farm_data?.name,
      wp.turbine_data?.turbine_number,
      wp.turbine_data?.location,
      wp.work_scope_data?.name,
      wp.work_scope_real,
    ].map((field) => String(field || "").toLowerCase());
    return searchFields.some((field) => field.includes(searchTerm.toLowerCase()));
  });

  // Get display text for selected order
  const getSelectedWorkPermitDisplay = () => {
    const selected = workPermits.find((wp) => wp.id === parseInt(selectedWorkPermitId));
    if (!selected) return "";
    return `${selected.order_number} - ${selected.work_permit_number}`;
  };

  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
  };

  const handleSave = async () => {
    if (!selectedWorkPermitId) {
      toast.error("Please select an order");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");

      // Get current order data
      const orderResponse = await axios.get(
        `${API_BASE_URL}/workpermit/${selectedWorkPermitId}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const order = orderResponse.data;

      // Create start and end dates with selected times
      const startDate = new Date(date);
      const [startHours, startMinutes] = startTime.split(":");
      startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

      const endDate = new Date(date);
      const [endHours, endMinutes] = endTime.split(":");
      endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

      // Update team's active periods
      const year = date.getFullYear();
      const weekNumber = getWeekNumber(date);

      const teamResponse = await axios.get(
        `${API_BASE_URL}/teams/${teamId}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const team = teamResponse.data;

      const activePeriods = { ...team.active_periods };
      if (!activePeriods[year]) {
        activePeriods[year] = [];
      }
      if (!activePeriods[year].includes(weekNumber)) {
        activePeriods[year].push(weekNumber);
        activePeriods[year].sort((a, b) => a - b);

        await axios.put(
          `${API_BASE_URL}/teams/${teamId}/`,
          {
            ...team,
            active_periods: activePeriods,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      // Update order with team, dates, and status (becomes a work permit)
      const updateData = {
        ...order,
        team: teamId,
        status: "planned",
        planned_date_start: startDate.toISOString(),
        planned_date_end: endDate.toISOString(),
      };

      const response = await axios.put(
        `${API_BASE_URL}/workpermit/${selectedWorkPermitId}/`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Order planned successfully");
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error planning order:", error);
      toast.error("Failed to plan order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Plan Order</DialogTitle>
          <div className="text-sm text-gray-500 mt-2">
            <div>Date: {date?.toLocaleDateString("en-GB")}</div>
            <div>Team: {teamName}</div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium">Select Order</label>
            <div className="relative mt-1" ref={dropdownRef}>
              {/* Dropdown trigger */}
              <div
                className={`flex items-center justify-between w-full pl-4 pr-2 py-2 border border-gray-300 rounded-md shadow-sm bg-white cursor-pointer
                  ${loading ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"}
                `}
                onClick={() => {
                  if (!loading) {
                    setIsDropdownOpen(!isDropdownOpen);
                  }
                }}
              >
                <span className={selectedWorkPermitId ? "text-gray-900" : "text-gray-500"}>
                  {selectedWorkPermitId ? getSelectedWorkPermitDisplay() : "-- Select an order --"}
                </span>
                <div className="flex items-center gap-1">
                  {selectedWorkPermitId && (
                    <X
                      className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedWorkPermitId("");
                      }}
                    />
                  )}
                  <ChevronDown
                    className={`h-4 w-4 text-gray-400 transition-transform ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </div>

              {/* Dropdown content */}
              {isDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                  {/* Search input */}
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search orders..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  {/* Options list */}
                  <div className="overflow-y-auto" style={{ maxHeight: "300px" }}>
                    {filteredWorkPermits.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No orders found
                      </div>
                    ) : (
                      filteredWorkPermits.map((wp) => (
                        <div
                          key={wp.id}
                          className={`px-4 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0
                            ${selectedWorkPermitId === wp.id.toString() ? "bg-blue-50" : "hover:bg-gray-50"}
                          `}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedWorkPermitId(wp.id.toString());
                            setIsDropdownOpen(false);
                            setSearchTerm("");
                          }}
                        >
                          <div className="font-medium text-gray-900">
                            {wp.order_number} - {wp.work_permit_number}
                          </div>
                          <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                            <div>
                              <span className="font-medium">Windfarm:</span>{" "}
                              {wp.turbine_data?.wind_farm_data?.name || "N/A"}
                            </div>
                            <div>
                              <span className="font-medium">Turbine:</span>{" "}
                              {wp.turbine_data?.turbine_number || "N/A"} / {wp.turbine_data?.location || "N/A"}
                            </div>
                            <div>
                              <span className="font-medium">Work Scope:</span>{" "}
                              {wp.work_scope_data?.name === "Other"
                                ? `${wp.work_scope_real || "N/A"} (undefined)`
                                : wp.work_scope_data?.name || wp.work_scope_real || "N/A"}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full mt-1 p-2 border rounded text-sm"
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Time</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full mt-1 p-2 border rounded text-sm"
                disabled={loading}
              />
            </div>
          </div>

        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-white text-black rounded-full hover:bg-gray-100"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !selectedWorkPermitId}
            className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
          >
            {loading ? "Planning..." : "Plan Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickPlanModal;
